import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { ArrowRight, ArrowLeft, Check, ForkKnife } from "@phosphor-icons/react";

const ACTIVITY = [
  { v: "sedentary", l: "Sedentary", h: "Desk job, little exercise" },
  { v: "light", l: "Light", h: "1–3 sessions / week" },
  { v: "moderate", l: "Moderate", h: "3–5 sessions / week" },
  { v: "active", l: "Active", h: "6–7 sessions / week" },
  { v: "very_active", l: "Very active", h: "Manual job + training daily" },
];
const DIETARY = [
  { v: "none", l: "No restrictions" },
  { v: "vegetarian", l: "Vegetarian" },
  { v: "vegan", l: "Vegan" },
  { v: "pescatarian", l: "Pescatarian" },
  { v: "halal", l: "Halal" },
  { v: "kosher", l: "Kosher" },
  { v: "other", l: "Other" },
];
const MEALS = [
  { v: "2", l: "2 meals (IF)" },
  { v: "3", l: "3 meals" },
  { v: "4", l: "4 meals" },
  { v: "5", l: "5 meals" },
  { v: "if", l: "Intermittent fasting (custom)" },
];
const COOK_TIME = [
  { v: "15min", l: "15 min / day", h: "Quick assembly only" },
  { v: "30min", l: "30 min / day", h: "Standard prep" },
  { v: "60min", l: "60 min+ / day", h: "I like to cook" },
  { v: "shakes_only", l: "Shakes only", h: "I just want it easy" },
];
const ALCOHOL = [
  { v: "none", l: "None" },
  { v: "1-2_per_week", l: "1–2 / week" },
  { v: "3-5_per_week", l: "3–5 / week" },
  { v: "6+_per_week", l: "6+ / week" },
];

export default function NutritionPrefsStep({ onBack, onNext, setErr }) {
  const [age, setAge] = useState("");
  const [activity, setActivity] = useState("moderate");
  const [dietary, setDietary] = useState("none");
  const [dietaryOther, setDietaryOther] = useState("");
  const [allergies, setAllergies] = useState("");
  const [avoid, setAvoid] = useState("");
  const [mealsPerDay, setMealsPerDay] = useState("3");
  const [cookConf, setCookConf] = useState(3);
  const [cookTime, setCookTime] = useState("30min");
  const [alcohol, setAlcohol] = useState("none");
  const [typical, setTypical] = useState("");
  const [supps, setSupps] = useState("");
  const [meds, setMeds] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/client/nutrition-prefs");
        if (data) {
          setAge(String(data.age ?? ""));
          setActivity(data.activity_level || "moderate");
          setDietary(data.dietary_style || "none");
          setDietaryOther(data.dietary_style_other || "");
          setAllergies(data.allergies || "");
          setAvoid(data.foods_to_avoid || "");
          setMealsPerDay(data.meals_per_day || "3");
          setCookConf(data.cooking_confidence || 3);
          setCookTime(data.cooking_time_per_day || "30min");
          setAlcohol(data.alcohol_intake || "none");
          setTypical(data.typical_day_eating || "");
          setSupps(data.supplements || "");
          setMeds(data.medications || "");
        }
      } catch { /* first visit */ }
    })();
  }, []);

  const save = async () => {
    if (!age) { setErr("Age is required."); return; }
    const ageNum = Number(age);
    if (Number.isNaN(ageNum) || ageNum < 15 || ageNum > 99) { setErr("Age must be 15–99."); return; }
    setSaving(true); setErr("");
    try {
      await http.post("/client/nutrition-prefs", {
        age: ageNum,
        activity_level: activity,
        dietary_style: dietary,
        dietary_style_other: dietary === "other" ? (dietaryOther || null) : null,
        allergies: allergies || null,
        foods_to_avoid: avoid || null,
        meals_per_day: mealsPerDay,
        cooking_confidence: cookConf,
        cooking_time_per_day: cookTime,
        alcohol_intake: alcohol,
        typical_day_eating: typical || null,
        supplements: supps || null,
        medications: meds || null,
      });
      await http.post("/client/onboarding/complete", {});
      onNext();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="onboarding-step-nutrition">
      <div className="id-label mb-3">— Step 6 of 6 —</div>
      <div className="flex items-center gap-2 mb-2">
        <ForkKnife size={24} weight="bold" className="text-[#FF3333]" />
        <h1 className="id-h1">Your food world</h1>
      </div>
      <p className="text-zinc-400 font-manrope mb-8">
        Last step. The more honest you are here, the better your meal plan will fit your actual life — not somebody else&apos;s.
      </p>

      <div className="space-y-7">
        {/* Age */}
        <div>
          <label className="id-label block mb-2">Age</label>
          <input type="number" min={15} max={99} value={age} onChange={(e) => setAge(e.target.value)} className="id-input w-32" placeholder="e.g. 38" data-testid="nutr-age" />
          <div className="text-zinc-500 text-xs mt-1 font-manrope">Used to calculate your daily calorie target.</div>
        </div>

        {/* Activity */}
        <div>
          <label className="id-label block mb-2">Activity level</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="nutr-activity">
            {ACTIVITY.map((a) => (
              <button key={a.v} type="button" onClick={() => setActivity(a.v)} data-testid={`nutr-activity-${a.v}`}
                className={`py-3 px-3 border text-left transition-colors ${activity === a.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                <div className="font-oswald uppercase tracking-wide text-sm">{a.l}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5 font-manrope">{a.h}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Dietary style */}
        <div>
          <label className="id-label block mb-2">Dietary style</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="nutr-dietary">
            {DIETARY.map((d) => (
              <button key={d.v} type="button" onClick={() => setDietary(d.v)} data-testid={`nutr-dietary-${d.v}`}
                className={`py-3 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${dietary === d.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                {d.l}
              </button>
            ))}
          </div>
          {dietary === "other" && (
            <input value={dietaryOther} onChange={(e) => setDietaryOther(e.target.value)} className="id-input mt-3" placeholder="Tell us what — e.g. paleo, keto, FODMAP..." data-testid="nutr-dietary-other" />
          )}
        </div>

        {/* Allergies + Avoid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="id-label block mb-2">Allergies / intolerances</label>
            <input value={allergies} onChange={(e) => setAllergies(e.target.value)} className="id-input" placeholder="e.g. Lactose, peanuts, gluten" data-testid="nutr-allergies" />
          </div>
          <div>
            <label className="id-label block mb-2">Foods you hate</label>
            <input value={avoid} onChange={(e) => setAvoid(e.target.value)} className="id-input" placeholder="e.g. Mushrooms, oats, cottage cheese" data-testid="nutr-avoid" />
          </div>
        </div>

        {/* Meals per day */}
        <div>
          <label className="id-label block mb-2">Preferred meals per day</label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="nutr-meals">
            {MEALS.map((m) => (
              <button key={m.v} type="button" onClick={() => setMealsPerDay(m.v)} data-testid={`nutr-meals-${m.v}`}
                className={`py-3 border uppercase tracking-[0.15em] text-[10px] font-bold transition-colors ${mealsPerDay === m.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                {m.l}
              </button>
            ))}
          </div>
        </div>

        {/* Cooking confidence */}
        <div data-testid="nutr-cook-conf">
          <div className="flex items-baseline justify-between mb-2">
            <label className="id-label">Cooking confidence</label>
            <div className="font-oswald text-2xl text-[#FF3333] font-bold">{cookConf}<span className="text-zinc-600 text-base">/5</span></div>
          </div>
          <input type="range" min={1} max={5} step={1} value={cookConf} onChange={(e) => setCookConf(Number(e.target.value))} className="w-full accent-[#FF3333]" />
          <div className="flex justify-between text-[10px] text-zinc-600 uppercase tracking-[0.15em] mt-1">
            <span>Can&apos;t cook</span><span>Restaurant level</span>
          </div>
        </div>

        {/* Cook time */}
        <div>
          <label className="id-label block mb-2">Time to cook per day</label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="nutr-cooktime">
            {COOK_TIME.map((c) => (
              <button key={c.v} type="button" onClick={() => setCookTime(c.v)} data-testid={`nutr-cooktime-${c.v}`}
                className={`py-3 px-3 border text-left transition-colors ${cookTime === c.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                <div className="font-oswald uppercase tracking-wide text-sm">{c.l}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5 font-manrope">{c.h}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Alcohol */}
        <div>
          <label className="id-label block mb-2">Alcohol intake</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="nutr-alcohol">
            {ALCOHOL.map((a) => (
              <button key={a.v} type="button" onClick={() => setAlcohol(a.v)} data-testid={`nutr-alcohol-${a.v}`}
                className={`py-3 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${alcohol === a.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                {a.l}
              </button>
            ))}
          </div>
          <div className="text-zinc-500 text-xs mt-2 font-manrope">No judgement — your plan will work with whatever you tell me, not against it.</div>
        </div>

        {/* Typical day */}
        <div>
          <label className="id-label block mb-2">What does a typical day of eating look like right now?</label>
          <textarea value={typical} onChange={(e) => setTypical(e.target.value)} rows={4} className="id-input" placeholder="Breakfast: usually skip&#10;Lunch: sandwich + chips&#10;Dinner: whatever the kids are having + a beer&#10;Snacks: probably too many" data-testid="nutr-typical" />
          <div className="text-zinc-500 text-xs mt-1 font-manrope">Honest beats impressive. This helps me design around your real habits.</div>
        </div>

        {/* Supplements */}
        <div>
          <label className="id-label block mb-2">Supplements you&apos;re already taking <span className="text-zinc-600 normal-case">(optional)</span></label>
          <input value={supps} onChange={(e) => setSupps(e.target.value)} className="id-input" placeholder="e.g. Whey protein, creatine, magnesium, fish oil" data-testid="nutr-supps" />
        </div>

        {/* Medications */}
        <div>
          <label className="id-label block mb-2">Medications you&apos;re currently on <span className="text-zinc-600 normal-case">(optional)</span></label>
          <input value={meds} onChange={(e) => setMeds(e.target.value)} className="id-input" placeholder="e.g. Blood pressure, antidepressants, thyroid, GLP-1, etc." data-testid="nutr-meds" />
          <div className="text-zinc-500 text-xs mt-1 font-manrope">Stays private between you and your coach. Helps Jimmy plan around anything that affects appetite, energy or recovery.</div>
        </div>
      </div>

      <div className="mt-10 flex justify-between">
        <button type="button" onClick={onBack} className="id-btn-secondary" data-testid="nutr-back"><ArrowLeft size={14} weight="bold" /> Back</button>
        <button type="button" onClick={save} disabled={saving} className="id-btn-primary" data-testid="nutr-finish">
          {saving ? "Saving..." : "Finish onboarding"} <Check size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
